import { useCallback, useEffect, useLayoutEffect, type RefObject } from 'react';
import {
  availableHeight,
  fieldHeight,
  fieldMaxHeight,
  keyboardInset,
} from './composerHeight';

/**
 * Sizes the question field to its text within the limits of composerHeight,
 * again whenever the text, the window, the visual viewport (on-screen
 * keyboard, zoom) or the orientation changes. The keyboard's height is
 * published as `--keyboard-inset` on the chat window, which pads its bottom
 * so the composer and Send stay above the keyboard.
 */
export const useFieldHeight = ({
  field,
  transcript,
  host,
  value,
}: {
  field: RefObject<HTMLTextAreaElement>;
  /** The conversation the field may take height from. */
  transcript: RefObject<HTMLElement> | null;
  /** Receives `--keyboard-inset`. */
  host: RefObject<HTMLElement> | null;
  value: string;
}) => {
  const measure = useCallback(() => {
    const element = field.current;
    if (!element) return;
    const viewport = window.visualViewport;
    const visibleHeight = viewport?.height ?? window.innerHeight;
    // A pinch zoom also shrinks the visual viewport; only an unzoomed one
    // means an on-screen keyboard.
    const zoomed = !!viewport && Math.abs(viewport.scale - 1) > 0.01;
    const inset = zoomed
      ? 0
      : keyboardInset({
          layoutHeight: window.innerHeight,
          visualHeight: visibleHeight,
          visualOffsetTop: viewport?.offsetTop ?? 0,
        });
    host?.current?.style.setProperty('--keyboard-inset', `${inset}px`);

    const current = element.getBoundingClientRect().height;
    const transcriptHeight =
      transcript?.current?.getBoundingClientRect().height ?? null;
    const max = fieldMaxHeight({
      visibleHeight,
      available:
        transcriptHeight == null
          ? null
          : availableHeight({ transcriptHeight, fieldHeight: current }),
    });
    // Measure the content at the minimum height, then apply.
    element.style.height = '0px';
    const content = element.scrollHeight;
    const next = fieldHeight(content, max);
    element.style.height = `${next.height}px`;
    element.style.overflowY = next.scrolls ? 'auto' : 'hidden';
    element.dataset.maxHeight = String(max);
  }, [field, host, transcript]);

  useLayoutEffect(() => {
    measure();
  }, [measure, value]);

  useEffect(() => {
    const viewport = window.visualViewport;
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    viewport?.addEventListener('resize', schedule);
    viewport?.addEventListener('scroll', schedule);
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(schedule);
    if (transcript?.current) observer?.observe(transcript.current);
    // Fonts and the text size preference change the line height.
    void document.fonts?.ready.then(schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
      viewport?.removeEventListener('resize', schedule);
      viewport?.removeEventListener('scroll', schedule);
      observer?.disconnect();
    };
  }, [measure, transcript]);

  return measure;
};
